/* ───────────────────────────────────────────────────────────────────────────
   LiveStatus — at-a-glance status panel for one TUAP student.

   Used inside the TuapAdmin Detail view. Shows:
   - Onboarding %  (live from onbAnswers)
   - WhatsApp group created (yes/no)
   - Per-person join status (student / mother / father)
   - Welcome message sent (yes / not yet)
   - Group picture set (yes / not yet)
   - Profile meeting (Ravisha toggle: done / not done)

   The "joined" flags are powered by `groupMembers` (array of JIDs) written
   on the student doc by Ravisha's bot whenever someone joins or leaves the
   group. If a phone number isn't on the student record we render "—".
   ─────────────────────────────────────────────────────────────────────────── */

import { useState } from "react";
import {
  liveStatus, setProfileMeetingDone, queueWelcome, queueDirect,
} from "./tuap";

const Y = "#F2B100", YD = "#8A6400";
const INK = "#1A1A1E", MID = "#5C5C66", SUB = "#9A9AA2";
const BORDER = "#E6E6E9", CARD = "#FFFFFF";
const GREEN = "#16A34A", RED = "#D9534F";

function tsAgo(t) {
  if (!t) return "";
  const d = t.toDate ? t.toDate() : new Date(t);
  if (isNaN(d)) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return mins + "m ago";
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + "h ago";
  return Math.round(hrs / 24) + "d ago";
}

function Pill({ tone, children, mono }) {
  const colours = {
    green: { fg: GREEN, bg: "#DCFCE7" },
    gold:  { fg: YD, bg: "#FEF3C7" },
    grey:  { fg: SUB, bg: "#F0F0F4" },
    red:   { fg: RED, bg: "#FEE2E2" },
  };
  const c = colours[tone] || colours.grey;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11.5, fontWeight: 800, color: c.fg, background: c.bg,
      padding: "4px 10px", borderRadius: 999,
      fontFamily: mono ? "'Space Mono',monospace" : "'Archivo',sans-serif",
      letterSpacing: mono ? ".04em" : 0,
      textTransform: mono ? "uppercase" : "none" }}>
      {children}
    </span>
  );
}

function Dot({ state }) {
  // green=in, red=not, grey=unknown (no phone)
  const c = state === true ? GREEN : state === false ? RED : SUB;
  return (
    <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999,
      background: c, boxShadow: `0 0 0 3px ${c}22`, flexShrink: 0 }} />
  );
}

export default function LiveStatus({ student, onbLive }) {
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const s = liveStatus(student);
  // Prefer the LIVE new-onboarding progress (otg-2026 onboardingProfiles) over
  // the stale local onbAnswers copy that liveStatus() reads.
  const onbDone = !!(onbLive && onbLive.done) || !!s.onbComplete;
  const onbPct = onbLive && onbLive.started ? onbLive.pct : (onbDone ? 100 : 0);

  function flash(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  }

  async function nudgeMissing() {
    setBusy(true);
    try {
      // DM anyone who hasn't joined yet, asking them to use the invite link.
      const link = student.whatsappLink || "(link)";
      let count = 0;
      for (const r of s.joined) {
        if (r.in === false && r.phone) {
          await queueDirect({
            phone: r.phone,
            text: `Hi from NM Squad. We've created the WhatsApp group for ` +
              `${student.studentName || "the student"} but you haven't joined yet. ` +
              `Tap here to join: ${link}`,
          });
          count++;
        }
      }
      flash(count ? `Nudged ${count} ` + (count === 1 ? "person" : "people")
        : "Everyone's already in the group");
    } finally { setBusy(false); }
  }

  async function resendWelcome() {
    setBusy(true);
    try {
      await queueWelcome(student.id);
      flash("Welcome message + group picture queued");
    } finally { setBusy(false); }
  }

  async function toggleMeeting() {
    setBusy(true);
    try {
      await setProfileMeetingDone(student.id, !s.profileMeetingAt);
      flash(!s.profileMeetingAt ? "Profile meeting marked done"
        : "Profile meeting reset to pending");
    } finally { setBusy(false); }
  }

  const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12,
    padding: 18 };
  const tile = { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10,
    padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 };
  const grid = { display: "grid", gap: 10,
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" };

  return (
    <div style={card}>
      {/* top row: ONB% + status pills */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 200px" }}>
          <div style={{ flex: 1, height: 9, background: "#EEE", borderRadius: 999, minWidth: 100 }}>
            <div style={{ width: `${onbPct}%`, height: "100%",
              background: onbDone ? GREEN : Y, borderRadius: 999,
              transition: "width .3s" }} />
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 900, color: onbDone ? GREEN : YD,
            minWidth: 36, textAlign: "right" }}>
            {onbPct}%
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: SUB, letterSpacing: ".04em",
            fontFamily: "'Space Mono',monospace", textTransform: "uppercase" }}>onboarding</span>
        </div>
        {s.groupCreated ? <Pill tone="green" mono>Group live</Pill>
          : <Pill tone="grey" mono>No group</Pill>}
        {s.groupCreated && !s.linkReady && <Pill tone="gold" mono>No invite link</Pill>}
        {s.welcomeSentAt ? <Pill tone="green" mono>Welcome sent</Pill>
          : s.groupCreated ? <Pill tone="gold" mono>Welcome pending</Pill> : null}
        {s.pictureSetAt ? <Pill tone="green" mono>Pic set</Pill>
          : s.groupCreated ? <Pill tone="gold" mono>Pic pending</Pill> : null}
        {s.profileMeetingAt ? <Pill tone="green" mono>Meeting done</Pill>
          : <Pill tone="grey" mono>Meeting TBD</Pill>}
      </div>

      {/* join roster */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11.5, fontWeight: 800, color: SUB, letterSpacing: ".06em",
          fontFamily: "'Space Mono',monospace", textTransform: "uppercase", marginBottom: 8 }}>
          Joined the WhatsApp group
        </div>
        <div style={grid}>
          {s.joined.map(r => (
            <div key={r.key} style={tile}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Dot state={r.in} />
                <span style={{ fontSize: 13.5, fontWeight: 800, color: INK }}>{r.label}</span>
              </div>
              <div style={{ fontSize: 12, color: r.phone ? MID : SUB,
                fontFamily: "'Space Mono',monospace" }}>
                {r.phone || "no phone on file"}
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 700,
                color: r.in === true ? GREEN : r.in === false ? RED : SUB }}>
                {r.in === true ? "In the group"
                  : r.in === false ? "Not joined"
                  : r.phone ? "Not reported yet"
                  : "—"}
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: SUB, marginTop: 6,
          fontFamily: "'Space Mono',monospace" }}>
          {student.groupMembersUpdatedAt
            ? `updated ${tsAgo(student.groupMembersUpdatedAt)}`
            : s.groupCreated
              ? "the bot has not reported this group's members yet"
              : ""}
        </div>
      </div>

      {/* actions */}
      <div style={{ marginTop: 18, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={nudgeMissing}
          title={!s.linkReady ? "There is no invite link to send them"
            : !s.membersKnown ? "The bot has not reported who is in the group yet"
            : ""}
          disabled={busy || !s.groupCreated || !s.linkReady || !s.membersKnown || s.joinedCount === s.expected}
          style={{ background: "#25D366", color: "#fff", border: "none", borderRadius: 8,
            padding: "9px 16px", fontSize: 13, fontWeight: 800, cursor: "pointer",
            opacity: (busy || !s.groupCreated || !s.linkReady || !s.membersKnown || s.joinedCount === s.expected) ? 0.5 : 1 }}>
          Nudge missing joiners
        </button>
        <button onClick={resendWelcome} disabled={busy || !s.groupCreated}
          style={{ background: Y, color: YD, border: "none", borderRadius: 8,
            padding: "9px 16px", fontSize: 13, fontWeight: 800, cursor: "pointer",
            opacity: (busy || !s.groupCreated) ? 0.5 : 1 }}>
          {s.welcomeSentAt ? "Resend welcome + pic" : "Send welcome + pic"}
        </button>
        <button onClick={toggleMeeting} disabled={busy}
          style={{ background: s.profileMeetingAt ? "#FEE2E2" : "#DCFCE7",
            color: s.profileMeetingAt ? RED : GREEN, border: "none", borderRadius: 8,
            padding: "9px 16px", fontSize: 13, fontWeight: 800, cursor: "pointer",
            opacity: busy ? 0.5 : 1 }}>
          {s.profileMeetingAt ? "Unmark profile meeting" : "Mark profile meeting done"}
        </button>
      </div>

      {toast && (
        <div style={{ marginTop: 12, padding: "8px 12px", background: "#DCFCE7",
          color: GREEN, borderRadius: 8, fontSize: 12.5, fontWeight: 800,
          display: "inline-block" }}>{toast}</div>
      )}
    </div>
  );
}
